import { reaction, runInAction, when } from "mobx";
import { initComponentStore } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { t } from "@/i18n";

const LANDSCAPE_QUERY = "(min-aspect-ratio: 1)";
import { TOUCH_MEDIA_QUERY, isTouchDevice } from "@/utils/device";
import type { GitStatusEntry } from "@/app_store/git";
import { type Tab, evictAndAddTab } from "@/components/toolkit/Tabs/store";
import { ContextMenuStore } from "@/components/toolkit/ContextMenu/store";
import { PhysicalRepositoryStore, PhysicalRepositoryWorkspaceStore, ManagedRepositoryStore, ManagedRepositoryWorkspaceStore, ChangeEntryStore, RepositoryChangeStore, SandboxSessionStore } from "@/app_store/agent";
import { MergeConflictError, MergeRequiredError, IdentityRequiredError } from "@/app_store/errors";


export interface DiffContent {
    original: string;
    modified: string;
    language: string;
}

export type ReviewLoadState = "idle" | "loading" | "loaded" | "no-git" | "agent-offline";

// Inverse of RepositoryChange.from_labels()'s _LABEL_MARKER/_LABEL_TYPE tables
// (agent/agent/interfaces/repository/change.py) -- lets the UI keep using the
// old index_*/wt_* label vocabulary while the actual data now comes from the
// generated ChangeEntry (marker, type) pair.
const ENTRY_TO_LABEL: Record<string, string> = {
    "staged:added": "index_new",
    "staged:modified": "index_modified",
    "staged:deleted": "index_deleted",
    "staged:renamed": "index_renamed",
    "staged:typechange": "index_typechange",
    "untracked:added": "wt_new",
    "unstaged:modified": "wt_modified",
    "unstaged:deleted": "wt_deleted",
    "unstaged:renamed": "wt_renamed",
    "unstaged:typechange": "wt_typechange",
    "unstaged:unreadable": "wt_unreadable",
    "conflicted:modified": "conflicted",
    "resolved:modified":   "resolved",
};

function entryToLabel(entry: ChangeEntryStore): string {
    return ENTRY_TO_LABEL[`${entry.marker}:${entry.type}`] ?? entry.marker;
}

function changeToEntry(change: RepositoryChangeStore): GitStatusEntry {
    return { path: change.path, flags: 0, labels: change.entries.map(entryToLabel) };
}

// Only these two container variants (see @/app_store/agent/Session.generated's `container`
// union) carry a `changes`/stage/unstage/commit/revert/diff surface -- Sandbox (no repo
// detected) and ManagedRepositoryWorktree (not reachable as a top-level session container
// today) don't.
type GitContainer = PhysicalRepositoryWorkspaceStore | ManagedRepositoryWorkspaceStore;

function asGitContainer(container: unknown): GitContainer | null {
    if (container instanceof PhysicalRepositoryStore) return container.workspace;
    if (container instanceof ManagedRepositoryStore) return container.workspace;
    if (container instanceof PhysicalRepositoryWorkspaceStore || container instanceof ManagedRepositoryWorkspaceStore) return container;
    return null;
}

function asGitRepo(container: unknown): PhysicalRepositoryStore | ManagedRepositoryStore | null {
    if (container instanceof PhysicalRepositoryStore) return container;
    if (container instanceof ManagedRepositoryStore) return container;
    return null;
}

const EXT_TO_LANGUAGE: Record<string, string> = {
    ts: "typescript", tsx: "typescript",
    js: "javascript", jsx: "javascript", mjs: "javascript", cjs: "javascript",
    py: "python", rs: "rust", go: "go", java: "java", kt: "kotlin",
    cs: "csharp", cpp: "cpp", cc: "cpp", cxx: "cpp", c: "c", h: "c", hpp: "cpp",
    rb: "ruby", php: "php", swift: "swift",
    html: "html", htm: "html", css: "css", scss: "scss", less: "less",
    json: "json", jsonc: "json", yaml: "yaml", yml: "yaml",
    toml: "ini", xml: "xml", sql: "sql",
    sh: "shell", bash: "shell", zsh: "shell",
    md: "markdown", dockerfile: "dockerfile", tf: "hcl",
    proto: "protobuf", graphql: "graphql", gql: "graphql",
    lua: "lua", r: "r", scala: "scala",
};

function getLanguage(path: string): string {
    const filename = path.split("/").pop() ?? "";
    const lower = filename.toLowerCase();
    if (lower === "dockerfile") return "dockerfile";
    const ext = lower.split(".").pop() ?? "";
    return EXT_TO_LANGUAGE[ext] ?? "plaintext";
}


export type FileAction = "stage" | "unstage" | "revert";

export class ReviewSectionStore {
    contextMenuStore = new ContextMenuStore();
    openTabs: Tab[] = [];
    activeTabPath: string | null = null;
    diffContents: Map<string, DiffContent> = new Map();
    diffLoadingPaths: Set<string> = new Set();
    diffErrors: Set<string> = new Set();
    commitMessage: string = "";
    isCommitting: boolean = false;
    commitError: string | null = null;
    fileToRemove: string | null = null;
    // In-flight stage/unstage/revert per path; the row shows a preloader in place of its actions.
    pendingFileActions: Map<string, FileAction> = new Map();
    syncedFileWithNoChanges: string | null = null;
    isMobile: boolean = isTouchDevice();
    isLandscape: boolean = window.matchMedia(LANDSCAPE_QUERY).matches;
    isPushing: boolean = false;
    isPulling: boolean = false;
    // Editable modified (right) side of the diff: live edits per path + mobile edit-mode gate.
    editedModified: Map<string, string> = new Map();
    editModes: Set<string> = new Set();
    isSavingReview: boolean = false;
    operationError: string | null = null;
    mergeRequired: boolean = false;
    isAborting: boolean = false;
    identityRequired: boolean = false;
    identityName: string = "";
    identityEmail: string = "";
    isSavingIdentity: boolean = false;
    _pendingCommitMessage: string | null = null;
    revertAllDialogOpen: boolean = false;
    private _pathsToRevert: string[] = [];
    // Lazily-created sandbox session used to write edited files back to the repo
    // working tree; the workspace watcher then refreshes `changes`.
    private _sandboxSessionId: string | null = null;

    get mobileAccordionIndex() { return appStore.state.mobileAccordionIndex; }
    set mobileAccordionIndex(v: number) { appStore.state.mobileAccordionIndex = v; }
    get editorFontSize(): number {
        const settings = appStore.account.settings;
        return (this.isMobile ? settings?.editor_font_size_mobile : settings?.editor_font_size_desktop) ?? 13;
    }

    setEditorFontSize(size: number) {
        if (this.isMobile) {
            appStore.account.saveSettings({ editor_font_size_mobile: size });
        } else {
            appStore.account.saveSettings({ editor_font_size_desktop: size });
        }
    }

    get splitSizes(): number[] {
        const s = appStore.account.settings?.ui_split_sizes;
        return s && s.length > 0 ? s : [15, 85];
    }

    setSplitSizes(sizes: number[]) {
        appStore.account.saveSettings({ ui_split_sizes: sizes });
    }

    private _tabsLoaded!: boolean;

    constructor() {
        initComponentStore(this);
        this._tabsLoaded = false;

        window.matchMedia(TOUCH_MEDIA_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isMobile = e.matches; });
        });

        window.matchMedia(LANDSCAPE_QUERY).addEventListener("change", (e) => {
            runInAction(() => { this.isLandscape = e.matches; });
        });

        // Load persisted tabs from project
        const project = appStore.project.selectedProject;
        if (project) {
            const rawPaths = this.isMobile ? (project.open_tabs_review_mobile ?? []) : (project.open_tabs_review ?? []);
            const maxTabs = this.isMobile
                ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
                : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
            const savedPaths = rawPaths.length > maxTabs ? rawPaths.slice(rawPaths.length - maxTabs) : rawPaths;
            const rawActive = this.isMobile ? (project.active_tab_path_review_mobile || null) : (project.active_tab_path_review || null);
            const savedActive = rawActive && savedPaths.includes(rawActive) ? rawActive : (savedPaths[savedPaths.length - 1] ?? null);
            runInAction(() => {
                this.openTabs = savedPaths.map(path => ({
                    path,
                    label: path.split("/").filter(Boolean).pop() ?? path,
                }));
                this.activeTabPath = savedActive;
            });
        }
        this._tabsLoaded = true;

        // Save tabs when they change
        reaction(
            () => this.openTabs.map(t => t.path).join(",") + "|" + (this.activeTabPath ?? ""),
            () => {
                if (!this._tabsLoaded) return;
                appStore.project.updateReviewOpenTabs(
                    this.openTabs.map(t => t.path),
                    this.activeTabPath,
                    this.isMobile,
                );
            }
        );

        // Reload tabs when project changes
        reaction(
            () => appStore.project.selectedProjectId,
            () => {
                this._tabsLoaded = false;
                const project = appStore.project.selectedProject;
                const rawPaths = project
                    ? (this.isMobile ? (project.open_tabs_review_mobile ?? []) : (project.open_tabs_review ?? []))
                    : [];
                const maxTabs = this.isMobile
                    ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
                    : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
                const savedPaths = rawPaths.length > maxTabs ? rawPaths.slice(rawPaths.length - maxTabs) : rawPaths;
                const rawActive = project
                    ? (this.isMobile ? (project.active_tab_path_review_mobile || null) : (project.active_tab_path_review || null))
                    : null;
                const savedActive = rawActive && savedPaths.includes(rawActive) ? rawActive : (savedPaths[savedPaths.length - 1] ?? null);
                runInAction(() => {
                    this.openTabs = savedPaths.map(path => ({
                        path,
                        label: path.split("/").filter(Boolean).pop() ?? path,
                    }));
                    this.activeTabPath = savedActive;
                    this.diffContents = new Map();
                    this.diffErrors = new Set();
                });
                this._tabsLoaded = true;
            }
        );

        // Invalidate diff cache when git status changes so diffs stay accurate.
        // Also: if the currently-displayed "no changes" file just appeared in git
        // status (e.g. user saved it), auto-switch to showing its diff.
        // last_modified is included so saves to already-modified files (same labels)
        // still invalidate the diff cache.
        reaction(
            () => this._changes.map(c => c.path + ":" + c.last_modified + ":" + changeToEntry(c).labels.join(",")).join("|"),
            () => {
                console.log(`[ReviewSectionStore] git status changed, clearing diff cache`, {
                    staged: this.stagedEntries.length,
                    unstaged: this.unstagedEntries.length,
                    untracked: this.untrackedEntries.length,
                    activeTabPath: this.activeTabPath,
                });
                const noChangesPath = this.syncedFileWithNoChanges;
                if (noChangesPath) {
                    const nowHasChanges = [
                        ...this.conflictEntries,
                        ...this.resolvedConflictEntries,
                        ...this.stagedEntries,
                        ...this.unstagedEntries,
                        ...this.untrackedEntries,
                    ].some(e => e.path === noChangesPath);
                    if (nowHasChanges) {
                        runInAction(() => {
                            this.syncedFileWithNoChanges = null;
                            this._addTab(noChangesPath);
                            this.activeTabPath = noChangesPath;
                            this.diffContents = new Map();
                            this.diffErrors = new Set();
                        });
                        this._loadDiff(noChangesPath);
                        return;
                    }
                }
                runInAction(() => {
                    this.diffContents = new Map();
                    this.diffErrors = new Set();
                });
                if (this.activeTabPath) {
                    this._loadDiff(this.activeTabPath);
                }
            }
        );

        reaction(
            () => appStore.state.repoActionRequest,
            (req) => { if (req) this._runRepoAction(req.action); }
        );

        // Direct open-in-review request from AgentPane widgets (bypasses git status check).
        reaction(
            () => appStore.state.openInReviewRequest,
            (req) => {
                if (!req) return;
                appStore.state.setActiveFilePath(req.path);
                if (this.isMobile) {
                    runInAction(() => { this.mobileAccordionIndex = 1; });
                }
            }
        );

        // Sync active file from CodeSection: when global state changes to a relative
        // path that exists in git status, open/switch to that diff.
        reaction(
            () => appStore.state.activeFilePath,
            (relativePath) => {
                if (!relativePath) return;
                if (relativePath === this.activeTabPath) return;
                const inGitStatus = [
                    ...this.conflictEntries,
                    ...this.resolvedConflictEntries,
                    ...this.stagedEntries,
                    ...this.unstagedEntries,
                    ...this.untrackedEntries,
                ].some(e => e.path === relativePath);
                if (!inGitStatus) {
                    runInAction(() => {
                        this.syncedFileWithNoChanges = relativePath;
                        this.activeTabPath = null;
                        if (this.isMobile) this.mobileAccordionIndex = 1;
                    });
                    return;
                }
                runInAction(() => {
                    this.syncedFileWithNoChanges = null;
                    this._addTab(relativePath);
                    this.activeTabPath = relativePath;
                    if (this.isMobile) this.mobileAccordionIndex = 1;
                });
                if (!this.diffContents.has(relativePath) && !this.diffLoadingPaths.has(relativePath)) {
                    this._loadDiff(relativePath);
                }
            }
        );
    }

    get loadState(): ReviewLoadState {
        if (!appStore.project.selectedProject) return "idle";
        if (!appStore.agent.isConnected) return "agent-offline";
        if (!appStore.agent.session.container) return "loading";
        return asGitContainer(appStore.agent.session.container) ? "loaded" : "no-git";
    }

    get currentBranch(): string | null {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return null;
        return container.name;
    }

    private get _changes(): RepositoryChangeStore[] {
        if (appStore.state.vaultMode) return [];
        return asGitContainer(appStore.agent.session.container)?.changes ?? [];
    }

    // Conflicted files (from the `conflicted` GIT_STATUS marker) get their own
    // group and are excluded from the staged/unstaged/untracked groups below.
    get conflictEntries(): GitStatusEntry[] {
        return this._changes.map(changeToEntry).filter(e => e.labels.includes("conflicted"));
    }

    get resolvedConflictEntries(): GitStatusEntry[] {
        return this._changes.map(changeToEntry).filter(e => e.labels.includes("resolved"));
    }

    get stagedEntries(): GitStatusEntry[] {
        return this._changes.map(changeToEntry).filter(e => !e.labels.includes("conflicted") && e.labels.some(l => l.startsWith("index_")));
    }

    get unstagedEntries(): GitStatusEntry[] {
        return this._changes.map(changeToEntry).filter(e => !e.labels.includes("conflicted") && e.labels.some(l => l.startsWith("wt_") && l !== "wt_new"));
    }

    get untrackedEntries(): GitStatusEntry[] {
        return this._changes.map(changeToEntry).filter(e => !e.labels.includes("conflicted") && e.labels.includes("wt_new"));
    }

    get totalChanges(): number {
        return this._changes.length;
    }

    // MERGE_HEAD is set — a merge commit must be made to complete, even when
    // resolving the conflict left no net change (e.g. resolved to "ours").
    get isMerging(): boolean {
        return asGitContainer(appStore.agent.session.container)?.pending_merge ?? false;
    }

    // True when the current merge is a pull-merge AND local changes were stashed
    // before the pull, meaning a second merge/conflict phase may follow after commit.
    get pendingStashMerge(): boolean {
        const c = asGitContainer(appStore.agent.session.container);
        return c instanceof PhysicalRepositoryWorkspaceStore ? (c.pending_stash_merge ?? false) : false;
    }

    // Re-applying the pre-pull stash conflicted (2nd merge phase).
    get stashConflict(): boolean {
        const c = asGitContainer(appStore.agent.session.container);
        return c instanceof PhysicalRepositoryWorkspaceStore ? (c.stash_conflict ?? false) : false;
    }

    // Engine-decided: staged changes or pending merge commit present.
    get canCommit(): boolean {
        return asGitContainer(appStore.agent.session.container)?.can_commit ?? false;
    }

    get canAbort(): boolean {
        return asGitContainer(appStore.agent.session.container)?.pending_merge ?? false;
    }

    // --- editable modified side -------------------------------------------------

    // The modified (right) side maps to the index (not a file) only for a purely
    // staged diff -- that view stays read-only; everything else is a worktree diff.
    private get _activeStagedView(): boolean {
        const path = this.activeTabPath;
        if (!path) return false;
        const isUntracked = this.untrackedEntries.some(e => e.path === path);
        const isStaged = this.stagedEntries.some(e => e.path === path);
        const isUnstaged = this.unstagedEntries.some(e => e.path === path);
        const isConflict = this.conflictEntries.some(e => e.path === path);
        return isStaged && !isUntracked && !isUnstaged && !isConflict;
    }

    // Whether the active file may be edited at all (worktree diff). On mobile it
    // still requires toggling edit mode, mirroring CodeSection.
    get activeEditable(): boolean {
        if (!this.activeTabPath || this._activeStagedView) return false;
        if (this.isMobile && !this.editModes.has(this.activeTabPath)) return false;
        return true;
    }

    // True when the active file is a worktree diff that edit mode could enable
    // (used to show the mobile edit toggle).
    get activeCanEdit(): boolean {
        return !!this.activeTabPath && !this._activeStagedView;
    }

    get activeInEditMode(): boolean {
        return !this.isMobile || (!!this.activeTabPath && this.editModes.has(this.activeTabPath));
    }

    // Modified-side value shown in the diff: live edits if any, else the loaded diff.
    get modifiedValue(): string {
        const path = this.activeTabPath;
        if (!path) return "";
        if (this.editedModified.has(path)) return this.editedModified.get(path)!;
        return this.diffContents.get(path)?.modified ?? "";
    }

    get isActiveDirty(): boolean {
        const path = this.activeTabPath;
        if (!path || !this.editedModified.has(path)) return false;
        return this.editedModified.get(path) !== (this.diffContents.get(path)?.modified ?? "");
    }

    onModifiedChange(content: string) {
        if (this.activeTabPath) this.editedModified.set(this.activeTabPath, content);
    }

    toggleEditMode() {
        const path = this.activeTabPath;
        if (!path) return;
        if (this.editModes.has(path)) this.editModes.delete(path);
        else this.editModes.add(path);
    }

    // Live conflict regions for the active file, read from the streamed `conflicts`
    // model -- used by the Monaco merge-conflict CodeLens.
    getActiveConflicts(): any[] {
        const path = this.activeTabPath;
        if (!path) return [];
        const ws: any = asGitContainer(appStore.agent.session.container);
        return ws?.conflicts?.find((c: any) => c.path === path)?.regions ?? [];
    }

    private async _writeSession(): Promise<SandboxSessionStore | null> {
        const source = asGitRepo(appStore.agent.session.container)?.sandbox;
        if (!source) return null;
        const existing = this._sandboxSessionId ? source.sessions[this._sandboxSessionId] : null;
        if (existing) return existing;
        const created: { id: string } = await source.create_session();
        runInAction(() => { this._sandboxSessionId = created.id; });
        return source.sessions[created.id] ?? null;
    }

    async saveActive() {
        const path = this.activeTabPath;
        if (!path || this.isSavingReview) return;
        const content = this.editedModified.get(path) ?? this.diffContents.get(path)?.modified;
        if (content === undefined) return;
        this.isSavingReview = true;
        this.operationError = null;
        try {
            const session = await this._writeSession();
            if (!session) throw new Error("No sandbox");
            await session.write_file(path, content);
            // Clear in-memory edits so modifiedValue falls back to freshly-reloaded
            // diffContents (the workspace watcher fires shortly and reloads the diff).
            runInAction(() => { this.editedModified.delete(path); });
        } catch (e: any) {
            this.operationError = e?.message ?? "Save failed";
        } finally {
            this.isSavingReview = false;
        }
    }

    get projectName(): string | null {
        return appStore.project.selectedProject?.name ?? null;
    }

    get projectRelPath(): string | null {
        return appStore.project.selectedProject?.relative_path ?? null;
    }

    private _addTab(path: string) {
        const maxTabs = this.isMobile
            ? (appStore.account.settings?.max_open_tabs_mobile ?? 2)
            : (appStore.account.settings?.max_open_tabs_desktop ?? 10);
        this.openTabs = evictAndAddTab(this.openTabs, path, maxTabs);
    }

    // The path shown highlighted in the sidebar (active tab)
    get selectedFilePath(): string | null {
        return this.activeTabPath;
    }

    // Run a repo action requested from BranchSelect's menu. The outcome banners live
    // here, so jump to Review when there's something to show.
    async _runRepoAction(action: "push" | "pull" | "reset" | "refresh") {
        if (action === "push") await this.push();
        else if (action === "pull") await this.pull();
        else if (action === "refresh") await this.refresh();
        else await this.reset();
        if (this.operationError || this.mergeRequired || this.identityRequired) {
            appStore.state.requestSection("review");
        }
    }

    async reset() {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return;
        this.operationError = null;
        try {
            await appStore.state.trackRepoAction(() => container.reset());
        } catch (e: any) {
            this.operationError = e?.message ?? "Reset failed";
        }
    }

    async refresh() {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return;
        this.operationError = null;
        try {
            await appStore.state.trackRepoAction(() => container.refresh());
        } catch (e: any) {
            this.operationError = e?.message ?? "Refresh failed";
        }
    }

    dismissError() {
        this.operationError = null;
    }

    dismissMergeRequired() {
        this.mergeRequired = false;
    }

    dismissIdentity() {
        this.identityRequired = false;
        this._pendingCommitMessage = null;
    }

    async push() {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container || this.isPushing) return;
        this.isPushing = true;
        this.operationError = null;
        try {
            await appStore.state.trackRepoAction(() => container.push());
        } catch (e: any) {
            this.operationError = e?.message ?? "Push failed";
        } finally {
            this.isPushing = false;
        }
    }

    async pull(allowMerge: boolean = false) {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container || this.isPulling) return;
        this.isPulling = true;
        this.operationError = null;
        // A merge-pull keeps the "Start merge" banner up (button disabled) until it resolves.
        if (!allowMerge) this.mergeRequired = false;
        this.identityRequired = false;
        this._pendingCommitMessage = null;
        try {
            await appStore.state.trackRepoAction(() => container.pull("origin", allowMerge));
            this.mergeRequired = false;
        } catch (e: any) {
            this.mergeRequired = e instanceof MergeRequiredError;
            // Default pull can't fast-forward -> prompt the user to start a merge;
            // a merge needs a git identity first.
            if (e instanceof MergeRequiredError) {
                this.mergeRequired = true;
            } else if (e instanceof IdentityRequiredError) {
                this.identityRequired = true;
            } else if (e instanceof MergeConflictError) {
                // Expected outcome of a merge -- surfaced by the Conflicts group and
                // the in-progress/abort banner, so no extra error banner is shown.
            } else {
                this.operationError = e?.message ?? "Pull failed";
            }
        } finally {
            this.isPulling = false;
        }
    }

    get isRepoBusy(): boolean {
        return appStore.state.isRepoActionInProgress;
    }

    // Retry the pull with merging enabled (triggered by the "Start merge" button).
    startMerge() {
        if (this.isRepoBusy) return;
        return this.pull(true);
    }

    // Roll the merge-pull back to the exact pre-pull state (HEAD + uncommitted edits).
    async abortMerge() {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container || this.isAborting || this.isRepoBusy) return;
        this.isAborting = true;
        try {
            await appStore.state.trackRepoAction(() => container.abort_merge());
            this.operationError = null;
        } catch (e: any) {
            this.operationError = e?.message ?? "Abort failed";
        } finally {
            this.isAborting = false;
        }
    }

    // Save the committer identity, then resume the merge that required it.
    async saveIdentity() {
        const container = asGitContainer(appStore.agent.session.container);
        const name = this.identityName.trim();
        const email = this.identityEmail.trim();
        if (!container || this.isSavingIdentity || !name || !email) return;
        this.isSavingIdentity = true;
        try {
            await appStore.state.trackRepoAction(() => container.set_identity(name, email));
            this.identityRequired = false;
            if (this._pendingCommitMessage !== null) {
                const msg = this._pendingCommitMessage;
                this._pendingCommitMessage = null;
                await appStore.state.trackRepoAction(() => container.commit(msg));
                this.commitMessage = "";
            } else {
                await this.pull(true);
            }
        } catch (e: any) {
            this.operationError = e?.message ?? "Failed to set identity";
        } finally {
            this.isSavingIdentity = false;
        }
    }

    private async _mutateChanges(action: FileAction, paths: string[], op: (container: GitContainer, paths: string[]) => Promise<any>) {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return;
        // Skip rows already busy so a second click can't fire a conflicting request.
        const free = paths.filter(p => !this.pendingFileActions.has(p));
        if (free.length === 0) return;
        runInAction(() => { for (const p of free) this.pendingFileActions.set(p, action); });
        // No manual patch here: stage/unstage/revert reassign `changes` on the backend
        // (an @observable field), which streams back as a live session_change patch
        // (see @/api/handlers/session.ts) and updates `container.changes` on its own.
        this.operationError = null;
        try {
            await op(container, free);
        } catch (e: any) {
            // RPC forwards the backend exception message (see rpc_connection._dispatch_and_reply);
            // surface it in the changes-pane error banner.
            this.operationError = e?.message ?? "Operation failed";
        } finally {
            // Safe to clear on reply: the agent flushes the `changes` patch before responding.
            runInAction(() => { for (const p of free) this.pendingFileActions.delete(p); });
        }
    }

    promptRemoveFile(filePath: string) {
        this.fileToRemove = filePath;
    }

    cancelRemoveFile() {
        this.fileToRemove = null;
    }

    async confirmRemoveFile() {
        const path = this.fileToRemove;
        if (!path) return;
        this.fileToRemove = null;
        this.closeTab(path);
        // No dedicated "delete untracked file" RPC exists yet on the generated stores --
        // revert is the closest available primitive (a no-op for genuinely untracked files).
        await this._mutateChanges("revert", [path], (c, ps) => c.revert(ps));
    }

    stageFile(filePath: string) {
        this._mutateChanges("stage", [filePath], (c, ps) => c.stage(ps));
    }

    unstageFile(filePath: string) {
        this._mutateChanges("unstage", [filePath], (c, ps) => c.unstage(ps));
    }

    revertFile(filePath: string) {
        this._mutateChanges("revert", [filePath], (c, ps) => c.revert(ps));
    }

    stageEntries(paths: string[]) {
        if (paths.length === 0) return;
        this._mutateChanges("stage", paths, (c, ps) => c.stage(ps));
    }

    unstageEntries(paths: string[]) {
        if (paths.length === 0) return;
        this._mutateChanges("unstage", paths, (c, ps) => c.unstage(ps));
    }

    revertEntries(paths: string[]) {
        if (paths.length === 0) return;
        this._mutateChanges("revert", paths, (c, ps) => c.revert(ps));
    }

    // Revert is destructive, so it goes through a confirm dialog. We stash the
    // group's paths rather than reverting everything, so e.g. "revert all
    // unstaged" leaves untracked files alone.
    promptRevertGroup(paths: string[]) {
        this._pathsToRevert = paths;
        this.revertAllDialogOpen = true;
    }

    cancelRevertAll() {
        this.revertAllDialogOpen = false;
        this._pathsToRevert = [];
    }

    confirmRevertAll() {
        this.revertAllDialogOpen = false;
        const paths = this._pathsToRevert;
        this._pathsToRevert = [];
        this.revertEntries(paths);
    }

    async commit() {
        // A merge commit can proceed with no typed message (use a sensible default);
        // a normal commit still requires one.
        const msg = this.commitMessage.trim() || (this.isMerging ? "Merge branch" : "");
        if (!msg || this.isCommitting) return;
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return;
        this.isCommitting = true;
        this.commitError = null;
        try {
            // No manual patch: commit reassigns `changes` server-side, which streams back
            // as a live session_change patch (see @/api/handlers/session.ts).
            await appStore.state.trackRepoAction(() => container.commit(msg));
            this.commitMessage = "";
        } catch (e: any) {
            if (e instanceof IdentityRequiredError) {
                this._pendingCommitMessage = msg;
                this.identityRequired = true;
            } else {
                this.commitError = e?.message ?? "Commit failed";
            }
        } finally {
            this.isCommitting = false;
        }
    }

    selectFile(path: string) {
        appStore.state.setActiveFilePath(path);
        if (this.isMobile) {
            this.mobileAccordionIndex = 1;
        }
    }

    setActiveTab(path: string) {
        appStore.state.setActiveFilePath(path);
    }

    navigateTab(direction: -1 | 1) {
        if (this.openTabs.length === 0) return;
        const index = this.openTabs.findIndex(t => t.path === this.activeTabPath);
        const next = (index + direction + this.openTabs.length) % this.openTabs.length;
        this.setActiveTab(this.openTabs[next].path);
    }

    closeTab(path: string) {
        const index = this.openTabs.findIndex(t => t.path === path);
        if (index === -1) return;
        const next = this.openTabs.filter(t => t.path !== path);
        this.openTabs = next;
        if (this.activeTabPath === path) {
            const newPath = next[index]?.path ?? next[index - 1]?.path ?? null;
            this.activeTabPath = newPath;
            appStore.state.setActiveFilePath(newPath);
        }
        if (this.isMobile && next.length === 0) {
            this.mobileAccordionIndex = 0;
        }
    }

    reorderTabs(fromIndex: number, toIndex: number) {
        const tabs = [...this.openTabs];
        const [tab] = tabs.splice(fromIndex, 1);
        tabs.splice(toIndex, 0, tab);
        this.openTabs = tabs;
    }

    moveActiveTab(direction: -1 | 1) {
        if (!this.activeTabPath) return;
        const index = this.openTabs.findIndex(t => t.path === this.activeTabPath);
        if (index === -1) return;
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= this.openTabs.length) return;
        this.reorderTabs(index, newIndex);
    }

    closeAllTabs() {
        this.openTabs = [];
        this.activeTabPath = null;
        if (this.isMobile) {
            this.mobileAccordionIndex = 0;
        }
    }

    closeAllTabsExceptActive() {
        if (!this.activeTabPath) return;
        this.openTabs = this.openTabs.filter(t => t.path === this.activeTabPath);
    }

    private async _loadDiff(path: string) {
        const container = asGitContainer(appStore.agent.session.container);
        if (!container) return;

        const isUntracked = this.untrackedEntries.some(e => e.path === path);
        const isStaged = this.stagedEntries.some(e => e.path === path);
        const isUnstaged = this.unstagedEntries.some(e => e.path === path);
        const a = isUntracked ? "" : "HEAD";
        // Diff against the index (HEAD..index) only for a purely-staged file. If the
        // file also has unstaged edits, diff against the working tree (HEAD..worktree)
        // so the view reflects the current file instead of the stale staged snapshot.
        const staged = isStaged && !isUntracked && !isUnstaged;

        const containerType = container.constructor.name;
        console.log(`[ReviewSectionStore] _loadDiff start`, { path, isUntracked, isStaged, a, staged, containerType });
        runInAction(() => {
            this.diffLoadingPaths.add(path);
            this.diffErrors.delete(path);
        });
        try {
            const data = await container.diff(path, a, staged);
            console.log(`[ReviewSectionStore] _loadDiff done`, {
                path, a, staged,
                originalLen: data.original?.length ?? 0,
                modifiedLen: data.modified?.length ?? 0,
                originalSnippet: (data.original ?? "").slice(0, 80),
                data,
            });
            runInAction(() => {
                this.diffContents.set(path, {
                    original: data.original ?? "",
                    modified: data.modified ?? "",
                    language: getLanguage(path),
                });
            });
        } catch (e) {
            console.error(`[ReviewSectionStore] _loadDiff error`, { path, error: e });
            runInAction(() => {
                this.diffErrors.add(path);
            });
        } finally {
            runInAction(() => { this.diffLoadingPaths.delete(path); });
        }
    }

    // Agent-offline "Retry": re-select the project to reconnect.
    retry() {
        const id = appStore.project.selectedProjectId;
        if (id) appStore.project.selectProject(id);
    }
}
